import Promise from 'bluebird';
import _intersection from 'lodash/intersection';
import { Api, Route } from 'enums';
import { PageAPI } from 'lib/api';
import { scoreBuckets } from 'lib/scorekeeper';
import { initAdsConfig } from 'ads/actions';
import { checkOneIdAuthentication } from 'common/actions/oneIdActions';
import { getFan } from 'common/actions/fanActions';
import routeSelector from 'selectors/routeSelector';

const { Page: { EVENT_QUERY_KEYS } } = Api;

export const CLEAR_PAGE = 'CLEAR_PAGE';
export const REQUEST_PAGE = 'REQUEST_PAGE';
export const RECEIVE_PAGE = 'RECEIVE_PAGE';
export const INVALIDATE_PAGE = 'INVALIDATE_PAGE';
export const LAUNCH_PLAYER = 'LAUNCH_PLAYER';
export const PAGE_INFO_SET = 'PAGE_INFO_SET';
export const START_PAGE_SCORING = 'START_PAGE_SCORING';
export const COMPLETE_PAGE_SCORING = 'COMPLETE_PAGE_SCORING';
export const REFRESH_BUCKETS = 'REFRESH_BUCKETS';
export const REPLACE_HERO = 'REPLACE_HERO';

export function clearPage() {
    return {
        type: CLEAR_PAGE
    };
}

export function createRequestPageAction(id) {
    return {
        type: REQUEST_PAGE,
        id,
        requestedTime: Date.now()
    };
}

export function createReceivePageAction({
    id,
    name,
    edition,
    buckets,
    tracking,
    heroContent,
    lang,
    error
}) {
    return {
        type: RECEIVE_PAGE,
        id,
        name,
        edition,
        buckets,
        tracking,
        heroContent,
        lang,
        error,
        receivedTime: Date.now()
    };
}


export function startPageScoring() {
    return {
        type: START_PAGE_SCORING,
        startTime: Date.now()
    };
}

export function refreshBuckets(pageName, query) {
    return (dispatch, getState) => {
        const { app, scoring = {}, fan = {} } = getState();
        return PageAPI.getPage(pageName, query, app.lang, app.device, app.country)
            .then(({ page: { buckets } }) => {
                const influencers = scoring.influencers;
                const preferences = fan.preferences;
                // if not enough info to score, return regular buckets
                if (!buckets || !preferences || !influencers) {
                    dispatch({
                        type: REFRESH_BUCKETS,
                        buckets
                    });
                    return;
                }

                scoreBuckets(buckets, preferences, influencers)
                    .then((scoredBuckets) => {
                        dispatch({
                            type: REFRESH_BUCKETS,
                            buckets: scoredBuckets
                        });
                    });
            });
    };
}

export function completePageScoring(buckets) {
    return {
        type: COMPLETE_PAGE_SCORING,
        startTime: Date.now(),
        buckets
    };
}

export function bucketInBucketList(buckets = [], bucketPage) {
    const bucket = bucketPage.page.buckets[0];
    return buckets.some((bucketItem) => (bucketItem.name === bucket.name));
}

export function createFetchPageAction(id, params, query, lang, fullUrl) {
    return (dispatch, getState) => {
        const pageName = id;
        const promises = [];

        const { app: { device, country } } = getState();
        if (pageName === 'collection') {
            // page api no longer takes in bucketId as path param
            if (params) {
                query.bucketId = params.id;
                query.groupId = params.groupId;
            }
            query.ignores = 'displayLimit'; // We want to ignore displayLimit if building a collection page
            promises.push(PageAPI.getBucketPage(null, query, lang, device, country));
        } else {
            promises.push(PageAPI.getPage(pageName, query, lang, device, country));
        }

        if (_intersection(Object.keys(query), EVENT_QUERY_KEYS).length > 0) {
            promises.push(PageAPI.getEvent(params, query, lang, device, country));
        } else {
            promises.push(Promise.resolve(false));
        }

        if (pageName === 'player') {
            // full URL is used during route transitions. bucketId is used if coming in sideways
            if (fullUrl) {
                promises.push(PageAPI.getPageByFullUrl(fullUrl, device, country));
            } else if (query.bucketId) {
                promises.push(PageAPI.getBucketPage(null, query, lang, device, country));
            }
        } else {
            promises.push(Promise.resolve());
        }

        dispatch(createRequestPageAction(id));

        // promise.reflect() enables Promise.all() to always settle, even if one promise was rejected.
        return Promise.all(promises.map((promise) => promise.reflect()))
            .then(([pageInspection, eventInspection, bucketInspection]) => {
                // getPage should never be rejected, it resolves with an error page skeleton, so no need for isFulfilled().
                const { page: { name, edition = {},
                        buckets: originalBuckets = [],
                        tracking, error } = {} } = pageInspection.value() || {};

                // bluebird seems to return a reference to originally cached pageapi request to player
                // here's a trick to operate on a new reference
                const buckets = originalBuckets.concat([]);
                if (bucketInspection &&
                    bucketInspection.value() !== undefined &&
                    bucketInspection.value().page.buckets &&
                    !bucketInBucketList(buckets, bucketInspection.value())) {
                    const index = buckets.map(
                        (bucket) => (bucket.tags && bucket.tags.indexOf('replaceable') !== -1)
                    ).indexOf(true);
                    if (index !== -1) {
                        buckets[index] = bucketInspection.value().page.buckets[0];
                        buckets[index].__replaced = true;
                    }
                }

                let heroContent = null;
                if (eventInspection && eventInspection.isFulfilled() && eventInspection.value() !== false) {
                    heroContent = (eventInspection.value() !== undefined) ? eventInspection.value().page.contents : null;
                    if (!heroContent) {
                        return PageAPI.getVideo(query.id, {}, lang, device, country).then((videoResponse) => {
                            heroContent = (videoResponse && videoResponse.page) ?
                                videoResponse.page.contents :
                                buckets[0].contents && buckets[0].contents[0];
                            dispatch(
                                createReceivePageAction({
                                    id,
                                    name,
                                    edition,
                                    buckets,
                                    tracking,
                                    heroContent: { ...heroContent, concrete: true },
                                    lang,
                                    error
                                })
                            );
                            dispatch(initAdsConfig(pageName, tracking, query));
                        });
                    }
                    // if the hero was determined via id param, mark as concrete as to not change when scored
                    heroContent.concrete = true;
                }

                // last ditch effort to get hero content for the page
                if (!heroContent && buckets && buckets[0]) {
                    heroContent = buckets[0].contents && buckets[0].contents[0];
                }

                dispatch(createReceivePageAction({
                    id,
                    name,
                    edition,
                    buckets,
                    tracking,
                    heroContent,
                    lang,
                    error
                }));
                dispatch(initAdsConfig(pageName, tracking, query));
                return Promise.resolve();
            });
    };
}

export function createLaunchPlayerAction(content) {
    const id = content.id;
    const tileType = content.type || '';
    const source = content.sourceType === 'linear' ? content.source : null;

    return {
        type: LAUNCH_PLAYER,
        id,
        source,
        tileType,
        launchedAt: Date.now()
    };
}

export function scorePageBuckets() {
    return (dispatch, getState) => {
        const route = routeSelector(getState());
        // no need to score anything on schedule page
        if (route === Route.SCHEDULE) {
            return Promise.resolve();
        }
        dispatch(startPageScoring());
        return dispatch(checkOneIdAuthentication()).then((isAuthenticated) => {
            const state = getState();
            const buckets = state.page.buckets;
            if (!isAuthenticated) {
                dispatch(completePageScoring(buckets));
                return Promise.resolve(buckets);
            }
            const swid = state.oneId.swid;
            return dispatch(getFan(swid)).then(() => {
                const { scoring = {}, fan = {} } = getState();
                const influencers = scoring.influencers;
                const preferences = fan.preferences;
                if (!buckets || !preferences || !influencers) {
                    dispatch(completePageScoring(buckets));
                    return Promise.resolve(buckets);
                }
                return scoreBuckets(buckets, preferences, influencers)
                    .then((scoredBuckets) => {
                        dispatch(completePageScoring(scoredBuckets));
                        return Promise.resolve(scoredBuckets);
                    });
            }, () => {
                dispatch(completePageScoring(buckets));
                return Promise.resolve();
            });
        });
    };
}

export function setPageInfo(id, name) {
    return (dispatch) => {
        dispatch({
            type: PAGE_INFO_SET,
            id,
            name
        });
    };
}

export function replaceHero(content) {
    return {
        type: REPLACE_HERO,
        content
    };
}



// WEBPACK FOOTER //
// ./app/page/actions/index.js